import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, notifyMutation } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import Papa from "papaparse";
import { Plus, PencilSimple, Tag, ShieldWarning, MagnifyingGlass, DownloadSimple, Trash } from "@phosphor-icons/react";
import BulkImportDialog from "@/components/BulkImportDialog";
import { DeleteStockGuard, EditStockConfirm, fetchStockSummary } from "@/components/StockGuard";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const empty = {
  manufacturer_id: "", series_id: "", name: "", color_hex: "#1e293b",
  base_sku: "", inventory_type: "rolls", location: "",
  low_stock_threshold_meters: 5,
  cost_per_meter: "", price_per_meter: "", notes: "", discontinued: false, discontinued_at: "", full_roll_length_m: "",
};

export default function ProductsPage() {
  const { isAdmin, isStaff, canSeePricing, maskMoney } = useAuth();
  const [items, setItems] = useState([]);
  const [mfrs, setMfrs] = useState([]);
  const [series, setSeries] = useState([]);
  const [filterMfr, setFilterMfr] = useState("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [editStock, setEditStock] = useState(null);
  const [confirmEditOpen, setConfirmEditOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const canWrite = isAdmin || isStaff;

  const load = async () => {
    const params = {};
    if (filterMfr !== "all") params.manufacturer_id = filterMfr;
    const [{ data: p }, { data: m }, { data: s }] = await Promise.all([
      api.get("/products", { params }), api.get("/manufacturers"), api.get("/series"),
    ]);
    setItems(p.filter((x) => x.inventory_type !== "count"));
    setMfrs(m); setSeries(s);
  };
  useEffect(() => { load(); }, [filterMfr]);
  useEffect(() => { setSelected(new Set()); }, [filterMfr]);

  const mfrMap = Object.fromEntries(mfrs.map((m) => [m.id, m]));
  const seriesMap = Object.fromEntries(series.map((s) => [s.id, s]));
  const seriesForForm = series.filter((s) => !form.manufacturer_id || s.manufacturer_id === form.manufacturer_id);

  const terms = search.toLowerCase().split(/\s+/).filter(Boolean);
  const filtered = items.filter((p) => {
    if (terms.length === 0) return true;
    const hay = [
      p.name, p.base_sku, p.color_name,
      mfrMap[p.manufacturer_id]?.name, seriesMap[p.series_id]?.name,
    ].filter(Boolean).join(" ").toLowerCase();
    return terms.every((t) => hay.includes(t));
  });

  const openCreate = () => { setEditing(null); setEditStock(null); setForm({ ...empty, manufacturer_id: mfrs[0]?.id || "" }); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setEditStock(null);
    setForm({
      ...empty, ...p,
      cost_per_meter: p.cost_per_meter ?? "",
      price_per_meter: p.price_per_meter ?? "",
      full_roll_length_m: p.full_roll_length_m ?? "",
    });
    setOpen(true);
  };

  const doSave = async () => {
    setConfirmEditOpen(false);
    try {
      const payload = {
        ...form,
        inventory_type: "rolls",
        discontinued: !!form.discontinued,
        discontinued_at: form.discontinued_at ? form.discontinued_at : null,
        low_stock_threshold_meters: Number(form.low_stock_threshold_meters || 0),
        full_roll_length_m: form.full_roll_length_m === "" || form.full_roll_length_m == null ? null : Number(form.full_roll_length_m),
        cost_per_meter: form.cost_per_meter === "" ? null : Number(form.cost_per_meter),
        price_per_meter: form.price_per_meter === "" ? null : Number(form.price_per_meter),
      };
      const res = editing
        ? await api.put(`/products/${editing.id}`, payload)
        : await api.post("/products", payload);
      notifyMutation(res, editing ? "Updated" : "Created");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  const handleSave = async () => {
    if (!editing) { doSave(); return; }
    // Always re-validate fresh at save time (never trust cached/stale stock state).
    const stock = await fetchStockSummary("product", editing.id);
    if (stock.error) { toast.error("Couldn't verify stock — please try again."); return; }
    setEditStock(stock);
    if (stock.has_stock) { setConfirmEditOpen(true); return; }
    doSave();
  };

  const onDelete = async (p) => {
    try { const res = await api.delete(`/products/${p.id}`); notifyMutation(res, "Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const visibleIds = filtered.map((p) => p.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someVisibleSelected = visibleIds.some((id) => selected.has(id));
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const doBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const ids = [...selected];
      const { data } = await api.post("/products/bulk-delete", { ids });
      const parts = [];
      if (data.deleted_count) parts.push(`${data.deleted_count} deleted`);
      if (data.queued_count) parts.push(`${data.queued_count} sent for approval`);
      if (data.blocked_count) parts.push(`${data.blocked_count} skipped (have stock)`);
      if (data.error_count) parts.push(`${data.error_count} failed`);
      toast.success(parts.join(" · ") || "Nothing to delete");
      if (data.blocked_count) {
        toast.warning(`Skipped (active stock attached): ${data.blocked.map((b) => b.name).join(", ")}`);
      }
      setSelected(new Set());
      setBulkOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBulkDeleting(false); }
  };

  const exportCsv = async () => {
    if (filtered.length === 0) { toast.error("No products to export"); return; }
    // Fetch which products currently have real stock attached (active/incoming rolls
    // or units in stock) for the informational stock_assigned column.
    let flags = {};
    try { const { data } = await api.get("/products/stock-flags"); flags = data.flags || {}; }
    catch (e) { toast.error("Couldn't check stock status — exporting without it."); }
    const rows = filtered.map((p) => ({
      manufacturer_name: mfrMap[p.manufacturer_id]?.name || "",
      series_name: seriesMap[p.series_id]?.name || "",
      series_description: seriesMap[p.series_id]?.description || "",
      name: p.name || "",
      base_sku: p.base_sku || "",
      color_hex: p.color_hex || "",
      inventory_type: p.inventory_type || "rolls",
      full_roll_length_m: p.full_roll_length_m ?? "",
      discontinued: p.discontinued ? "Yes" : "No",
      discontinued_at: p.discontinued_at || "",
      stock_assigned: flags[p.id] ? "Yes" : "No",
      low_stock_threshold_meters: p.low_stock_threshold_meters ?? "",
      low_stock_threshold_count: p.low_stock_threshold_count ?? "",
      cost_per_meter: canSeePricing ? (p.cost_per_meter ?? "") : "",
      price_per_meter: canSeePricing ? (p.price_per_meter ?? "") : "",
      cost_per_unit: canSeePricing ? (p.cost_per_unit ?? "") : "",
      price_per_unit: canSeePricing ? (p.price_per_unit ?? "") : "",
      notes: p.notes || "",
    }));
    // Column order re-imports cleanly (Simple CSV needs manufacturer_name + base_sku).
    // series_description & stock_assigned are informational (ignored on re-import);
    // discontinued & full_roll_length_m ARE applied when re-imported with "Update existing".
    const columns = [
      "manufacturer_name", "series_name", "series_description", "name", "base_sku", "color_hex", "inventory_type",
      "full_roll_length_m", "discontinued", "discontinued_at", "stock_assigned", "low_stock_threshold_meters", "low_stock_threshold_count",
      "cost_per_meter", "price_per_meter", "cost_per_unit", "price_per_unit", "notes",
    ];
    const csv = Papa.unparse({ fields: columns, data: rows });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vwa-products-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} product${rows.length === 1 ? "" : "s"} to CSV`);
  };

  return (
    <div className="p-8 max-w-[1400px]" data-testid="products-page">
      <header className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">Catalogue</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900">Products</h1>
          <p className="text-sm text-slate-600 mt-2">Vinyl & tint colours. Stock levels are managed under <Link to="/rolls" className="text-blue-600 hover:underline">Inventory → Rolls</Link>.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportCsv} className="gap-2 rounded-md" data-testid="export-products-csv-btn">
            <DownloadSimple size={16} /> Export CSV
          </Button>
          {isAdmin && <BulkImportDialog onSuccess={load} />}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} disabled={series.length === 0} className="bg-blue-600 hover:bg-blue-700 gap-2 rounded-md" data-testid="add-product-btn">
                <Plus size={16} /> Add Product
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} Product</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Manufacturer</Label>
                    <Select value={form.manufacturer_id} onValueChange={(v) => setForm({ ...form, manufacturer_id: v, series_id: "" })}>
                      <SelectTrigger data-testid="prod-mfr-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                      <SelectContent>{mfrs.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Series</Label>
                    <Select value={form.series_id} onValueChange={(v) => setForm({ ...form, series_id: v })}>
                      <SelectTrigger data-testid="prod-series-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                      <SelectContent>{seriesForForm.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5 col-span-2">
                    <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Colour name</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Black Cherry Ice" data-testid="prod-name-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Colour swatch</Label>
                    <Input type="color" value={form.color_hex || "#000000"} onChange={(e) => setForm({ ...form, color_hex: e.target.value })} className="h-10 p-1" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Base SKU</Label>
                    <Input value={form.base_sku} onChange={(e) => setForm({ ...form, base_sku: e.target.value })} placeholder="3MVW2080G201" className="font-mono" data-testid="prod-sku-input" />
                  </div>
                  <div className="space-y-1.5 col-span-2">
                    <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Default location</Label>
                    <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Shelf A1" data-testid="prod-location-input" />
                    <div className="text-[10px] text-slate-500">New rolls inherit this location unless overridden.</div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Full roll length (m)</Label>
                    <Input type="number" step="0.01" value={form.full_roll_length_m}
                      onChange={(e) => setForm({ ...form, full_roll_length_m: e.target.value })} className="font-mono" placeholder="e.g. 18" data-testid="prod-full-roll-input" />
                    <div className="text-[10px] text-slate-500">Auto-fills the roll length when ordering this film.</div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Low stock threshold (m)</Label>
                    <Input type="number" step="0.01" value={form.low_stock_threshold_meters}
                      onChange={(e) => setForm({ ...form, low_stock_threshold_meters: e.target.value })} className="font-mono" />
                  </div>
                  {isAdmin && (
                    <>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Cost / m ($)</Label>
                        <Input type="number" step="0.01" value={form.cost_per_meter} onChange={(e) => setForm({ ...form, cost_per_meter: e.target.value })} className="font-mono" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Price / m ($)</Label>
                        <Input type="number" step="0.01" value={form.price_per_meter} onChange={(e) => setForm({ ...form, price_per_meter: e.target.value })} className="font-mono" />
                      </div>
                    </>
                  )}
                  <div className="space-y-1.5 col-span-2">
                    <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Notes</Label>
                    <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
                  </div>
                  <div className="col-span-2 flex items-center justify-between bg-slate-50 border border-slate-200 rounded-md p-3">
                    <div className="pr-3">
                      <div className="text-sm font-medium text-slate-800">Discontinued</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">Marks this product as no longer offered. It stays viewable and its stock is untouched, but it's flagged in the catalogue and skipped in low-stock alerts.</div>
                    </div>
                    <Switch checked={!!form.discontinued} onCheckedChange={(v) => setForm({ ...form, discontinued: v })} data-testid="prod-discontinued-switch" />
                  </div>
                  <div className="col-span-2 space-y-1.5">
                    <Label className="text-xs font-mono tracking-[0.15em] uppercase text-slate-600">Discontinued date (optional)</Label>
                    <Input type="date" value={form.discontinued_at || ""} onChange={(e) => setForm({ ...form, discontinued_at: e.target.value })} className="font-mono" data-testid="prod-discontinued-at-input" />
                    <p className="text-[11px] text-slate-500">Set a date when this product is or will be discontinued. Shows a “Disc. &lt;date&gt;” tag in the list — handy for products winding down soon.</p>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="rounded-md">Cancel</Button>
                <Button onClick={handleSave} disabled={!form.name || !form.base_sku || !form.manufacturer_id || !form.series_id}
                  className="bg-blue-600 hover:bg-blue-700 rounded-md" data-testid="prod-save-btn">
                  {isStaff ? "Submit for approval" : "Save"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      {isStaff && (
        <div className="flex items-center gap-2 border border-amber-200 bg-amber-50 text-amber-800 rounded-md px-4 py-2.5 mb-4 text-sm" data-testid="staff-approval-banner">
          <ShieldWarning size={18} /> Catalogue changes you make are sent to an admin for approval before they take effect.
        </div>
      )}

      <div className="relative mb-4 max-w-md">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search product, colour, brand, series or SKU…"
          className="pl-9 h-10 rounded-md bg-white"
          data-testid="product-search"
        />
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setFilterMfr("all")}
          className={`px-3 py-1.5 text-xs font-medium border rounded-md ${filterMfr === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200"}`}>All</button>
        {mfrs.map((m) => (
          <button key={m.id} onClick={() => setFilterMfr(m.id)}
            className={`px-3 py-1.5 text-xs font-medium border rounded-md ${filterMfr === m.id ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200"}`}>{m.name}</button>
        ))}
      </div>

      {canWrite && selected.size > 0 && (
        <div className="flex items-center gap-3 border border-blue-200 bg-blue-50 rounded-md px-4 py-2.5 mb-4" data-testid="bulk-action-bar">
          <span className="text-sm font-medium text-blue-900" data-testid="bulk-selected-count">{selected.size} selected</span>
          <Button onClick={() => setBulkOpen(true)} className="bg-red-600 hover:bg-red-700 gap-1.5 rounded-md h-9" data-testid="bulk-delete-btn">
            <Trash size={15} /> Delete selected
          </Button>
          <Button variant="ghost" onClick={() => setSelected(new Set())} className="h-9 rounded-md text-slate-600" data-testid="bulk-clear-btn">Clear</Button>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="border border-dashed border-slate-300 bg-white p-16 text-center">
          <Tag size={32} className="mx-auto text-slate-300 mb-3" />
          <div className="text-sm text-slate-500">{search ? "No products match your search." : "No products yet."}</div>
        </div>
      ) : (
        <div className="border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">
                {canWrite && (
                  <th className="px-4 py-3 w-10">
                    <Checkbox checked={allVisibleSelected ? true : (someVisibleSelected ? "indeterminate" : false)} onCheckedChange={toggleAll} aria-label="Select all" data-testid="bulk-select-all" />
                  </th>
                )}
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3">Manufacturer / Series</th>
                <th className="px-4 py-3">Base SKU</th>
                <th className="px-4 py-3 text-right">Roll Length</th>
                <th className="px-4 py-3 text-right">Low-stock thr.</th>
                {canSeePricing && <th className="px-4 py-3 text-right">Cost</th>}
                {canSeePricing && <th className="px-4 py-3 text-right">Price</th>}
                <th className="px-4 py-3 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className={`border-b border-slate-100 last:border-0 hover:bg-slate-50 ${selected.has(p.id) ? "bg-blue-50/50" : ""}`}>
                  {canWrite && (
                    <td className="px-4 py-3">
                      <Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggleOne(p.id)} aria-label={`Select ${p.name}`} data-testid={`bulk-select-${p.id}`} />
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Link to={`/browse/product/${p.id}`} className="flex items-center gap-3 hover:text-blue-600">
                        <div className="w-7 h-7 border border-slate-200 rounded-sm" style={{ background: p.color_hex || "#e2e8f0" }} />
                        <span className="font-medium">{p.name}</span>
                      </Link>
                      {p.discontinued && (
                        <span data-testid={`discontinued-badge-${p.id}`} className="text-[9px] font-mono uppercase tracking-[0.12em] bg-slate-200 text-slate-600 rounded px-1.5 py-0.5 shrink-0">Discontinued</span>
                      )}
                      {p.discontinued_at && (
                        <span data-testid={`discontinuing-badge-${p.id}`} className="text-[9px] font-mono uppercase tracking-[0.12em] bg-amber-100 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5 shrink-0" title={`Discontinued date: ${p.discontinued_at}`}>Disc. {p.discontinued_at}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{mfrMap[p.manufacturer_id]?.name} · {seriesMap[p.series_id]?.name}</td>
                  <td className="px-4 py-3 font-mono text-xs">{p.base_sku}</td>
                  <td className="px-4 py-3 font-mono text-right tabular-nums" data-testid={`roll-length-${p.id}`}>{p.full_roll_length_m != null && p.full_roll_length_m !== "" ? `${p.full_roll_length_m}m` : "—"}</td>
                  <td className="px-4 py-3 font-mono text-right tabular-nums">≤ {p.low_stock_threshold_meters}m</td>
                  {canSeePricing && <td className="px-4 py-3 font-mono text-right tabular-nums">{p.cost_per_meter != null ? maskMoney(`$${p.cost_per_meter.toFixed(2)}`) : "—"}</td>}
                  {canSeePricing && <td className="px-4 py-3 font-mono text-right tabular-nums text-blue-700">{p.price_per_meter != null ? maskMoney(`$${p.price_per_meter.toFixed(2)}`) : "—"}</td>}
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}><PencilSimple size={14} /></Button>
                      <DeleteStockGuard entityType="product" id={p.id} name={p.name} isStaff={isStaff} onConfirm={() => onDelete(p)} testId={`delete-product-${p.id}`} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <EditStockConfirm open={confirmEditOpen} onOpenChange={setConfirmEditOpen} entityType="product" name={editing?.name} summary={editStock?.summary} onConfirm={doSave} />

      <AlertDialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <AlertDialogContent data-testid="bulk-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} product{selected.size === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              {isStaff
                ? "These deletions will be sent to an admin for approval before they take effect."
                : "This permanently deletes the selected products. Any product that still has active stock attached will be skipped (not deleted)."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doBulkDelete} disabled={bulkDeleting} className="bg-red-600 hover:bg-red-700" data-testid="bulk-delete-confirm-btn">
              {bulkDeleting ? "Deleting…" : (isStaff ? "Send for approval" : "Delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
